import { useState } from 'react';
import { toast } from 'react-toastify';
import {
  CheckExistingParkingStallStorageLockers as checkSlotAvailabilityRequest,
  deleteProperty as deletePropertyRequest,
  fetchCalendlyLink as fetchCalendlyLinkRequest,
  fetchPropertyManagerDetailsForHomeowner as fetchPropertyManagerDetailsForHomeownerRequest,
  getBuildingLevels as getBuildingLevelsRequest,
  getIndividualPropertySuiteByID as getIndividualPropertySuiteByIDRequest,
  getProperties as getPropertiesRequest,
  getPropertyById as getPropertyByIdRequest,
  getPropertyBuildings as getPropertyBuildingsRequest,
  getSuiteOnBoardingConveyanceFormListByPropertyCSV as getConveyanceCsvRequest,
  updateProperty as updatePropertyRequest,
} from 'src/services/client';
import { serviceRequestTableLocalTime } from 'src/helpers/validationHelper';
import {
  Contact,
  Property,
  Suite,
} from 'src/SharedCustomHooks/UseSuiteActions/types';
import { ModulePermission } from 'src/components/ContactForm/types';
import { requestModulePermissions } from 'src/SharedCustomHooks/UseModulePermissionsAPI/helpers';

export interface CalendlyData {
  calendlyLink: string;
  calendlyAPIKey?: string;
}

interface UsePropertiesAPIState {
  properties: Property[];
  isLoadingProperties: boolean;
  propertiesError: string | null;

  property: Property | null;
  isLoadingProperty: boolean;
  propertyError: string | null;
  isUpdatingProperty: boolean;
  isDeletingProperty: boolean;

  conveyanceCsv: any[];

  propertySuites: Suite[];
  isLoadingPropertySuites: boolean;
  propertySuitesError: string | null;

  allowedTabList: ModulePermission[];
  isLoadingAllowedTabList: boolean;
  allowedTabListError: string | null;

  propertyManagerDetails: Contact | null;
  isLoadingPropertyManagerDetails: boolean;
  propertyManagerDetailsError: string | null;

  calendlyData: CalendlyData | null;
  isLoadingCalendlyLink: boolean;
  calendlyLinkError: string | null;

  buildings: any[];
  isLoadingBuildings: boolean;
  buildingsError: string | null;

  levels: any[];
  isLoadingLevels: boolean;
  levelsError: string | null;
}

export interface FetchPropertiesResult {
  ok: boolean;
  properties: Property[];
}

export interface FetchPropertyResult {
  ok: boolean;
  property: Property | null;
}

export interface UpdatePropertyResult {
  ok: boolean;
  property: Property | null;
}

export interface DeletePropertyResult {
  ok: boolean;
}

export interface FetchConveyanceCsvResult {
  ok: boolean;
  conveyanceCsv: any[];
}

export interface FetchPropertySuitesResult {
  ok: boolean;
  suites: Suite[];
}

export interface FetchAllowedTabListResult {
  ok: boolean;
  allowedTabList: ModulePermission[];
}

export interface FetchPropertyManagerDetailsResult {
  ok: boolean;
  propertyManagerDetails: Contact | null;
}

export interface FetchCalendlyLinkResult {
  ok: boolean;
  calendlyData: CalendlyData;
}

export interface FetchBuildingsResult {
  ok: boolean;
  buildings: any[];
}

export interface FetchLevelsResult {
  ok: boolean;
  levels: any[];
}

export type SlotType = 'ParkingStalls' | 'StorageLockers';

export interface CheckSlotAvailabilityResult {
  ok: boolean;
  available: boolean;
}

export interface UsePropertiesAPIReturn {
  properties: Property[];
  isLoadingProperties: boolean;
  propertiesError: string | null;
  fetchProperties: (
    userId: string,
    role: string
  ) => Promise<FetchPropertiesResult>;
  clearPropertiesError: () => void;

  property: Property | null;
  isLoadingProperty: boolean;
  propertyError: string | null;
  isUpdatingProperty: boolean;
  isDeletingProperty: boolean;
  fetchPropertyById: (id: string) => Promise<FetchPropertyResult>;
  updateProperty: (data: any) => Promise<UpdatePropertyResult>;
  deleteProperty: (id: string, userId: string) => Promise<DeletePropertyResult>;
  clearPropertyError: () => void;

  conveyanceCsv: any[];
  fetchConveyanceCsv: (
    propertyId: string
  ) => Promise<FetchConveyanceCsvResult>;

  propertySuites: Suite[];
  isLoadingPropertySuites: boolean;
  propertySuitesError: string | null;
  fetchPropertySuites: (
    propertyId: string,
    userId: string,
    role: string
  ) => Promise<FetchPropertySuitesResult>;
  clearPropertySuitesError: () => void;

  allowedTabList: ModulePermission[];
  isLoadingAllowedTabList: boolean;
  allowedTabListError: string | null;
  fetchAllowedTabList: (propertyId: string) => Promise<FetchAllowedTabListResult>;
  clearAllowedTabListError: () => void;

  propertyManagerDetails: Contact | null;
  isLoadingPropertyManagerDetails: boolean;
  propertyManagerDetailsError: string | null;
  fetchPropertyManagerDetailsForHomeowner: (
    propertyId: string
  ) => Promise<FetchPropertyManagerDetailsResult>;
  clearPropertyManagerDetailsError: () => void;

  calendlyData: CalendlyData | null;
  isLoadingCalendlyLink: boolean;
  calendlyLinkError: string | null;
  fetchCalendlyLink: (email: string) => Promise<FetchCalendlyLinkResult>;
  clearCalendlyLinkError: () => void;

  buildings: any[];
  isLoadingBuildings: boolean;
  buildingsError: string | null;
  fetchBuildingForProperty: (
    propertyId: string
  ) => Promise<FetchBuildingsResult>;
  clearBuildingsError: () => void;

  levels: any[];
  isLoadingLevels: boolean;
  levelsError: string | null;
  fetchLevelsForBuilding: (buildingId: string) => Promise<FetchLevelsResult>;
  clearLevelsError: () => void;

  checkSlotAvailability: (
    propertyId: string,
    name: string,
    type: SlotType
  ) => Promise<CheckSlotAvailabilityResult>;
}

const EMPTY_CALENDLY: CalendlyData = { calendlyLink: '' };

const unwrapArrayPayload = <T,>(result: any): T[] => {
  if (Array.isArray(result)) {
    return result;
  }

  return result?.data || [];
};

export const usePropertiesAPI = (): UsePropertiesAPIReturn => {
  const [state, setState] = useState<UsePropertiesAPIState>({
    properties: [],
    isLoadingProperties: false,
    propertiesError: null,

    property: null,
    isLoadingProperty: false,
    propertyError: null,
    isUpdatingProperty: false,
    isDeletingProperty: false,

    conveyanceCsv: [],

    propertySuites: [],
    isLoadingPropertySuites: false,
    propertySuitesError: null,

    allowedTabList: [],
    isLoadingAllowedTabList: false,
    allowedTabListError: null,

    propertyManagerDetails: null,
    isLoadingPropertyManagerDetails: false,
    propertyManagerDetailsError: null,

    calendlyData: null,
    isLoadingCalendlyLink: false,
    calendlyLinkError: null,

    buildings: [],
    isLoadingBuildings: false,
    buildingsError: null,

    levels: [],
    isLoadingLevels: false,
    levelsError: null,
  });

  const fetchProperties = async (
    userId: string,
    role: string
  ): Promise<FetchPropertiesResult> => {
    setState((prev: UsePropertiesAPIState) => ({
      ...prev,
      isLoadingProperties: true,
      propertiesError: null,
    }));

    try {
      const response: any = await getPropertiesRequest(userId, role);

      if (!response.ok) {
        throw new Error(`Failed to fetch properties: ${response.status}`);
      }

      const result: any = await response.json();
      const properties: Property[] = unwrapArrayPayload<Property>(result);

      setState((prev: UsePropertiesAPIState) => ({
        ...prev,
        properties,
        isLoadingProperties: false,
      }));

      return { ok: true, properties };
    } catch (err: any) {
      console.error('usePropertiesAPI fetchProperties failed:', err);

      const errorMessage: string = err?.message || 'Failed to fetch properties';

      toast.error(`🚫 ${errorMessage}`);

      setState((prev: UsePropertiesAPIState) => ({
        ...prev,
        isLoadingProperties: false,
        propertiesError: errorMessage,
      }));

      return { ok: false, properties: [] };
    }
  };

  const fetchPropertyById = async (
    id: string
  ): Promise<FetchPropertyResult> => {
    setState((prev: UsePropertiesAPIState) => ({
      ...prev,
      isLoadingProperty: true,
      propertyError: null,
    }));

    try {
      const response: any = await getPropertyByIdRequest(id);

      if (!response.ok && response.status !== 200) {
        throw new Error(`Failed to fetch property: ${response.status}`);
      }

      const property: Property | null = (await response.json()) || null;

      setState((prev: UsePropertiesAPIState) => ({
        ...prev,
        property,
        isLoadingProperty: false,
      }));

      return { ok: true, property };
    } catch (err: any) {
      console.error('usePropertiesAPI fetchPropertyById failed:', err);

      const errorMessage: string = err?.message || 'Failed to fetch property';

      toast.error(`🚫 ${errorMessage}`);

      setState((prev: UsePropertiesAPIState) => ({
        ...prev,
        isLoadingProperty: false,
        propertyError: errorMessage,
      }));

      return { ok: false, property: null };
    }
  };

  const updateProperty = async (
    data: any
  ): Promise<UpdatePropertyResult> => {
    setState((prev: UsePropertiesAPIState) => ({
      ...prev,
      isUpdatingProperty: true,
    }));

    try {
      const response: any = await updatePropertyRequest(data);

      if (!response.ok && response.status !== 200) {
        throw new Error(`Failed to update property: ${response.status}`);
      }

      const result: any = await response.json();
      const updated: Property | null = result?.data?.[0] || null;

      if (!updated) {
        throw new Error('Update response contained no property');
      }

      setState((prev: UsePropertiesAPIState) => ({
        ...prev,
        property: updated,
        isUpdatingProperty: false,
      }));

      toast.success(' ⓘ Property details have been updated.', {
        autoClose: 10000,
        position: 'top-center',
      });

      return { ok: true, property: updated };
    } catch (err: any) {
      console.error('usePropertiesAPI updateProperty failed:', err);

      toast.error(
        ' 🚫 Something went wrong with updating a property, please try again.',
        { autoClose: 12000, position: 'top-center' }
      );

      setState((prev: UsePropertiesAPIState) => ({
        ...prev,
        isUpdatingProperty: false,
      }));

      return { ok: false, property: null };
    }
  };

  const deleteProperty = async (
    id: string,
    userId: string
  ): Promise<DeletePropertyResult> => {
    setState((prev: UsePropertiesAPIState) => ({
      ...prev,
      isDeletingProperty: true,
    }));

    try {
      const response: any = await deletePropertyRequest(id, userId);

      if (!response.ok && response.status !== 200) {
        throw new Error(`Failed to delete property: ${response.status}`);
      }

      setState((prev: UsePropertiesAPIState) => ({
        ...prev,
        isDeletingProperty: false,
      }));

      toast.success('ⓘ Property has been deleted.', {
        autoClose: 10000,
        position: 'top-center',
      });

      return { ok: true };
    } catch (err: any) {
      console.error('usePropertiesAPI deleteProperty failed:', err);

      toast.error(
        ' 🚫 Something went wrong with deleting the property, please try again.',
        { autoClose: 12000, position: 'top-center' }
      );

      setState((prev: UsePropertiesAPIState) => ({
        ...prev,
        isDeletingProperty: false,
      }));

      return { ok: false };
    }
  };

  const fetchConveyanceCsv = async (
    propertyId: string
  ): Promise<FetchConveyanceCsvResult> => {
    try {
      const response: any = await getConveyanceCsvRequest(propertyId);

      if (!response.ok && response.status !== 200) {
        throw new Error(`Failed to fetch conveyance CSV: ${response.status}`);
      }

      const result: any = await response.json();

      if (result?.statusCode !== 200) {
        toast.error('🚫 Conveyance Form download failed, please try again.', {
          autoClose: 12000,
          position: 'top-center',
        });

        return { ok: false, conveyanceCsv: [] };
      }

      const rawData: any[] = result.data || [];
      const conveyanceCsv: any[] = rawData.map((item: any) => ({
        ...item,
        dateTime: serviceRequestTableLocalTime(item.date, 'contacts'),
      }));

      setState((prev: UsePropertiesAPIState) => ({
        ...prev,
        conveyanceCsv,
      }));

      return { ok: true, conveyanceCsv };
    } catch (err: any) {
      console.error('usePropertiesAPI fetchConveyanceCsv failed:', err);

      toast.error('🚫 Something went wrong , please try again.', {
        autoClose: 12000,
        position: 'top-center',
      });

      return { ok: false, conveyanceCsv: [] };
    }
  };

  const fetchPropertySuites = async (
    propertyId: string,
    userId: string,
    role: string
  ): Promise<FetchPropertySuitesResult> => {
    setState((prev: UsePropertiesAPIState) => ({
      ...prev,
      isLoadingPropertySuites: true,
      propertySuitesError: null,
    }));

    try {
      const response: any = await getIndividualPropertySuiteByIDRequest(
        propertyId,
        userId,
        role
      );

      if (response.status === 204) {
        setState((prev: UsePropertiesAPIState) => ({
          ...prev,
          propertySuites: [],
          isLoadingPropertySuites: false,
        }));

        return { ok: true, suites: [] };
      }

      if (response.status !== 200) {
        throw new Error(`Failed to fetch property suites: ${response.status}`);
      }

      const result: any = await response.json();
      const suites: Suite[] = unwrapArrayPayload<Suite>(result);

      setState((prev: UsePropertiesAPIState) => ({
        ...prev,
        propertySuites: suites,
        isLoadingPropertySuites: false,
      }));

      return { ok: true, suites };
    } catch (err: any) {
      console.error('usePropertiesAPI fetchPropertySuites failed:', err);

      const errorMessage: string =
        err?.message || 'Failed to fetch property suites';

      toast.error(`🚫 ${errorMessage}`);

      setState((prev: UsePropertiesAPIState) => ({
        ...prev,
        isLoadingPropertySuites: false,
        propertySuitesError: errorMessage,
      }));

      return { ok: false, suites: [] };
    }
  };

  const fetchAllowedTabList = async (
    propertyId: string
  ): Promise<FetchAllowedTabListResult> => {
    setState((prev: UsePropertiesAPIState) => ({
      ...prev,
      isLoadingAllowedTabList: true,
      allowedTabListError: null,
    }));

    try {
      const allowedTabList: ModulePermission[] =
        await requestModulePermissions(propertyId);

      setState((prev: UsePropertiesAPIState) => ({
        ...prev,
        allowedTabList,
        isLoadingAllowedTabList: false,
      }));

      return { ok: true, allowedTabList };
    } catch (err: any) {
      console.error('usePropertiesAPI fetchAllowedTabList failed:', err);

      const errorMessage: string = err?.message || 'Failed to fetch tab list';

      setState((prev: UsePropertiesAPIState) => ({
        ...prev,
        isLoadingAllowedTabList: false,
        allowedTabListError: errorMessage,
      }));

      return { ok: false, allowedTabList: [] };
    }
  };

  const fetchPropertyManagerDetailsForHomeowner = async (
    propertyId: string
  ): Promise<FetchPropertyManagerDetailsResult> => {
    setState((prev: UsePropertiesAPIState) => ({
      ...prev,
      isLoadingPropertyManagerDetails: true,
      propertyManagerDetailsError: null,
    }));

    try {
      const response: any =
        await fetchPropertyManagerDetailsForHomeownerRequest(propertyId);

      if (!response.ok) {
        throw new Error(
          `Failed to fetch property manager details: ${response.status}`
        );
      }

      const result: any = await response.json();
      const propertyManagerDetails: Contact | null =
        result?.data || result || null;

      setState((prev: UsePropertiesAPIState) => ({
        ...prev,
        propertyManagerDetails,
        isLoadingPropertyManagerDetails: false,
      }));

      return { ok: true, propertyManagerDetails };
    } catch (err: any) {
      console.error(
        'usePropertiesAPI fetchPropertyManagerDetailsForHomeowner failed:',
        err
      );

      const errorMessage: string =
        err?.message || 'Failed to fetch property manager details';

      toast.error(`🚫 ${errorMessage}`);

      setState((prev: UsePropertiesAPIState) => ({
        ...prev,
        isLoadingPropertyManagerDetails: false,
        propertyManagerDetailsError: errorMessage,
      }));

      return { ok: false, propertyManagerDetails: null };
    }
  };

  const fetchCalendlyLink = async (
    email: string
  ): Promise<FetchCalendlyLinkResult> => {
    setState((prev: UsePropertiesAPIState) => ({
      ...prev,
      isLoadingCalendlyLink: true,
      calendlyLinkError: null,
    }));

    try {
      const response: any = await fetchCalendlyLinkRequest(email);

      if (!response.ok) {
        throw new Error(`Failed to fetch calendly link: ${response.status}`);
      }

      const result: any = await response.json();

      // Backend wraps the link as `calendarLink` and 400 means "no calendly
      // configured for this PM" — both surface here as an empty link, not an error toast.
      if (result.statusCode === 200 && result.data) {
        const calendlyData: CalendlyData = {
          calendlyLink: result.data.calendarLink,
          calendlyAPIKey: result.data.calendlyAPIKey,
        };

        setState((prev: UsePropertiesAPIState) => ({
          ...prev,
          calendlyData,
          isLoadingCalendlyLink: false,
        }));

        return { ok: true, calendlyData };
      }

      setState((prev: UsePropertiesAPIState) => ({
        ...prev,
        calendlyData: EMPTY_CALENDLY,
        isLoadingCalendlyLink: false,
      }));

      return { ok: true, calendlyData: EMPTY_CALENDLY };
    } catch (err: any) {
      console.error('usePropertiesAPI fetchCalendlyLink failed:', err);

      const errorMessage: string =
        err?.message || 'Failed to fetch calendly link';

      toast.error(`🚫 ${errorMessage}`);

      setState((prev: UsePropertiesAPIState) => ({
        ...prev,
        isLoadingCalendlyLink: false,
        calendlyLinkError: errorMessage,
        calendlyData: EMPTY_CALENDLY,
      }));

      return { ok: false, calendlyData: EMPTY_CALENDLY };
    }
  };

  const fetchBuildingForProperty = async (
    propertyId: string
  ): Promise<FetchBuildingsResult> => {
    setState((prev: UsePropertiesAPIState) => ({
      ...prev,
      isLoadingBuildings: true,
      buildingsError: null,
    }));

    try {
      const response: any = await getPropertyBuildingsRequest(propertyId);

      if (!response.ok) {
        throw new Error(`Failed to fetch buildings: ${response.status}`);
      }

      const result: any = await response.json();
      const buildings: any[] = unwrapArrayPayload<any>(result);

      setState((prev: UsePropertiesAPIState) => ({
        ...prev,
        buildings,
        isLoadingBuildings: false,
      }));

      return { ok: true, buildings };
    } catch (err: any) {
      console.error('usePropertiesAPI fetchBuildingForProperty failed:', err);

      const errorMessage: string = err?.message || 'Failed to fetch buildings';

      toast.error(`🚫 ${errorMessage}`);

      setState((prev: UsePropertiesAPIState) => ({
        ...prev,
        isLoadingBuildings: false,
        buildingsError: errorMessage,
      }));

      return { ok: false, buildings: [] };
    }
  };

  const fetchLevelsForBuilding = async (
    buildingId: string
  ): Promise<FetchLevelsResult> => {
    setState((prev: UsePropertiesAPIState) => ({
      ...prev,
      isLoadingLevels: true,
      levelsError: null,
    }));

    try {
      const response: any = await getBuildingLevelsRequest(buildingId);

      if (!response.ok) {
        throw new Error(
          `Failed to fetch levels for ${buildingId}: ${response.status}`
        );
      }

      const result: any = await response.json();
      const levels: any[] = unwrapArrayPayload<any>(result);

      setState((prev: UsePropertiesAPIState) => ({
        ...prev,
        levels,
        isLoadingLevels: false,
      }));

      return { ok: true, levels };
    } catch (err: any) {
      console.error('usePropertiesAPI fetchLevelsForBuilding failed:', err);

      const errorMessage: string =
        err?.message || 'Failed to fetch levels for building';

      toast.error(`🚫 ${errorMessage}`);

      setState((prev: UsePropertiesAPIState) => ({
        ...prev,
        isLoadingLevels: false,
        levelsError: errorMessage,
      }));

      return { ok: false, levels: [] };
    }
  };

  // One-shot validation, not a data fetch — owns the fetch-error toast and
  // returns availability for the caller to act on. `true` from the endpoint means
  // the slot is already taken, so availability is its inverse.
  const checkSlotAvailability = async (
    propertyId: string,
    name: string,
    type: SlotType
  ): Promise<CheckSlotAvailabilityResult> => {
    try {
      const response: any = await checkSlotAvailabilityRequest(
        propertyId,
        name,
        type
      );

      if (!response.ok) {
        throw new Error(`Failed to validate slot: ${response.status}`);
      }

      const isTaken: boolean = (await response.json()) === true;

      return { ok: true, available: !isTaken };
    } catch (err: any) {
      console.error('usePropertiesAPI checkSlotAvailability failed:', err);

      toast.error('🚫 Something went wrong with the slot validation.');

      return { ok: false, available: false };
    }
  };

  const clearPropertiesError = () => {
    setState((prev: UsePropertiesAPIState) => ({
      ...prev,
      propertiesError: null,
    }));
  };

  const clearPropertyError = () => {
    setState((prev: UsePropertiesAPIState) => ({
      ...prev,
      propertyError: null,
    }));
  };

  const clearPropertySuitesError = () => {
    setState((prev: UsePropertiesAPIState) => ({
      ...prev,
      propertySuitesError: null,
    }));
  };

  const clearAllowedTabListError = () => {
    setState((prev: UsePropertiesAPIState) => ({
      ...prev,
      allowedTabListError: null,
    }));
  };

  const clearPropertyManagerDetailsError = () => {
    setState((prev: UsePropertiesAPIState) => ({
      ...prev,
      propertyManagerDetailsError: null,
    }));
  };

  const clearCalendlyLinkError = () => {
    setState((prev: UsePropertiesAPIState) => ({
      ...prev,
      calendlyLinkError: null,
    }));
  };

  const clearBuildingsError = () => {
    setState((prev: UsePropertiesAPIState) => ({
      ...prev,
      buildingsError: null,
    }));
  };

  const clearLevelsError = () => {
    setState((prev: UsePropertiesAPIState) => ({
      ...prev,
      levelsError: null,
    }));
  };

  return {
    properties: state.properties,
    isLoadingProperties: state.isLoadingProperties,
    propertiesError: state.propertiesError,
    fetchProperties,
    clearPropertiesError,

    property: state.property,
    isLoadingProperty: state.isLoadingProperty,
    propertyError: state.propertyError,
    isUpdatingProperty: state.isUpdatingProperty,
    isDeletingProperty: state.isDeletingProperty,
    fetchPropertyById,
    updateProperty,
    deleteProperty,
    clearPropertyError,

    conveyanceCsv: state.conveyanceCsv,
    fetchConveyanceCsv,

    propertySuites: state.propertySuites,
    isLoadingPropertySuites: state.isLoadingPropertySuites,
    propertySuitesError: state.propertySuitesError,
    fetchPropertySuites,
    clearPropertySuitesError,

    allowedTabList: state.allowedTabList,
    isLoadingAllowedTabList: state.isLoadingAllowedTabList,
    allowedTabListError: state.allowedTabListError,
    fetchAllowedTabList,
    clearAllowedTabListError,

    propertyManagerDetails: state.propertyManagerDetails,
    isLoadingPropertyManagerDetails: state.isLoadingPropertyManagerDetails,
    propertyManagerDetailsError: state.propertyManagerDetailsError,
    fetchPropertyManagerDetailsForHomeowner,
    clearPropertyManagerDetailsError,

    calendlyData: state.calendlyData,
    isLoadingCalendlyLink: state.isLoadingCalendlyLink,
    calendlyLinkError: state.calendlyLinkError,
    fetchCalendlyLink,
    clearCalendlyLinkError,

    buildings: state.buildings,
    isLoadingBuildings: state.isLoadingBuildings,
    buildingsError: state.buildingsError,
    fetchBuildingForProperty,
    clearBuildingsError,

    levels: state.levels,
    isLoadingLevels: state.isLoadingLevels,
    levelsError: state.levelsError,
    fetchLevelsForBuilding,
    clearLevelsError,

    checkSlotAvailability,
  };
};
